/* eslint-disable @nx/enforce-module-boundaries */
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { ImageInputComponent } from 'admin-drivers/src/app/common/components/image-input/image-input.component';
import { SafeUrl } from '@angular/platform-browser';
import { BaseDialogStepperFormComponent } from 'admin-drivers/src/app/common/dialog/base-dialog-stepper-form';
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { User } from 'admin-drivers/src/app/common/models/User';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserService } from 'admin-drivers/src/app/api/services/user.service';
import { tap, delay } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 't-llevo-personal-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatIconModule,
    MatButtonModule,
    ImageInputComponent,
    FormsModule,
    MatAutocompleteModule,
  ],
  templateUrl: './personal-form.component.html',
  styles: `
    @import '../../../../utils/styles/formGroup.scss';

    mat-form-field:nth-child(-n + 7) {
      display: flex;
      width: 49% !important;
    }

    t-llevo-image-input {
      width: 100%;
      min-height: 200px;
      max-height: 350px;
    }
  `,
})
export class PersonalFormComponent
  extends BaseDialogStepperFormComponent<FormGroup>
  implements AfterViewInit, OnInit
{
  @ViewChild('firstInput') elementRef!: ElementRef<HTMLInputElement>;
  @ViewChild('input') input!: ElementRef<HTMLInputElement>;

  private _userSrv = inject(UserService);
  private _destroyRef = inject(DestroyRef);

  private allUsers: User[] = [];
  protected autocompleteOptions: User[] = [];
  private date = new Date();
  protected maxDate = new Date(
    this.date.getFullYear() - 18,
    this.date.getMonth(),
    this.date.getDate(),
  );
  protected showUserSelection = false;
  croppedImage: SafeUrl | undefined | null = null;

  get selectedUser() {
    return this.frm.controls['selectedUser']
  }

  get name() {
    return this.frm.controls['name'];
  }

  get lastname() {
    return this.frm.controls['lastname'];
  }

  get email() {
    return this.frm.controls['email'];
  }

  get phone() {
    return this.frm.controls['phone'];
  }

  get birthday() {
    return this.frm.controls['birthday'];
  }

  get dni() {
    return this.frm.controls['dni'];
  }

  get photo() {
    return this.frm.controls['photo'] as FormControl<File | null>;
  }

  ngOnInit(): void {
    this.loaderId = this.loaderSrv.subscribe();
    this._userSrv.getUsers({roles: 'client', limit: 9999})
      .pipe(takeUntilDestroyed(this._destroyRef))
      .pipe(
        tap(() => {
          this.loaderSrv.show(this.loaderId);
        }),
        delay(100),
      )
      .subscribe({
        next: (data) => {
          this.allUsers = data.results;
          this.autocompleteOptions = data.results;
          this.loaderSrv.hide(this.loaderId);
        },
      });
  }

  ngAfterViewInit() {
    this.loaderSrv.loaderState$.subscribe((data) => {
      if (!data && this.focusOnFirstInput && this.loaderSrv.updatedCount > 0) {
        this.elementRef.nativeElement.focus();
      }
    });
  }

  protected displayValue(data: User): string {
    return data ? `${data.firstname} ${data.lastname}` : '';
  }

  protected filter(): void {
    const filterValue = this.input.nativeElement.value.toLowerCase();
    this.autocompleteOptions = this.allUsers.filter(
      (user) =>
        user.firstname.toLowerCase().includes(filterValue) ||
        user.lastname.toLowerCase().includes(filterValue),
    );
  }

  protected selectUser(event: MatAutocompleteSelectedEvent) {
    const value = event.option.value as User;

    this.name.setValue(value.firstname);
    this.lastname.setValue(value.lastname);
    this.email.setValue(value.email);
    this.phone.setValue(value.phone);
  }

  protected unselectUser() {
    this.selectedUser.setValue(null)
    this.clearUserData();
  }

  protected toogleUserSelection(event: MatCheckboxChange) {
    if (event.checked) {
      this.disableUserControls()
      this.selectedUser.addValidators([Validators.required])
    } else {
      this.unselectUser()
      this.enableUserControls();
      this.selectedUser.clearValidators()
      this.selectedUser.updateValueAndValidity()
    }
  }

  protected clearUserData() {
    this.name.reset();
    this.lastname.reset();
    this.email.reset();
    this.phone.reset();
  }

  protected enableUserControls() {
    this.name.enable();
    this.lastname.enable();
    this.email.enable();
    this.phone.enable();
  }

  protected disableUserControls(){
    this.name.disable();
    this.lastname.disable();
    this.email.disable();
    this.phone.disable();
  }
}
